import React, { useEffect, useRef, useContext } from 'react';
import { loadGist } from './InnerHtmlGist';

// Placeholder ID
let placeholderScriptId = 0;
const nextPlaceholderId = () => {
  return 'placeholder_script_' + placeholderScriptId++;
};

type ContextValues = {
  addScript: (script: HTMLScriptElement, placeholder: HTMLElement) => void;
};
const Context = React.createContext<ContextValues>({} as any);

export const Provider: React.FC = ({ children }) => {
  const p = useRef(Promise.resolve());

  function addScript(script: HTMLScriptElement, placeholder: HTMLElement) {
    p.current = p.current.then(function loadScript() {
      return new Promise(function elevioAddScriptInner(res) {
        if (script.src) {
          // Github Gist
          if (script.src.match(/gist.github.com/)) {
            loadGist(script.src, placeholder);
            res();
            return;
          }
          script.onload = () => res();
          script.onerror = () => res();

          placeholder.insertAdjacentElement('afterend', script);
          return;
        } else {
          placeholder.insertAdjacentElement('afterend', script);
          res();
        }
      });
    });
  }

  return <Context.Provider value={{ addScript }}>{children}</Context.Provider>;
};

type Props = {
  html: string;
} & React.HTMLAttributes<HTMLDivElement>;

const InnerHtml: React.FC<Props> = (props) => {
  const { html, ...rest } = props;
  const divRef = useRef<HTMLDivElement>(null);
  const scriptContext = useContext(Context);

  useEffect(() => {
    if (!html) return;

    // Create a 'tiny' document and parse the html string
    const slotHtml = document.createRange().createContextualFragment(html);

    // Scripts don't wait for the previous ones to load, so remove them from the document fragment
    // and manually load them ourselves.
    const scripts = slotHtml.querySelectorAll('script');
    scripts.forEach((s) => {
      const placeholderScript = document.createElement('script');
      placeholderScript.id = nextPlaceholderId();
      s.parentNode?.replaceChild(placeholderScript, s);
      scriptContext.addScript(s, placeholderScript);
    });

    // Insert custom html (without scripts)
    if (divRef.current) {
      divRef.current.innerHTML = ''; // Clear the container
      divRef.current.appendChild(slotHtml); // Append the new content
    }
  }, [html, scriptContext]);

  return <div {...rest} ref={divRef}></div>;
};

export default InnerHtml;
